import React, { useState, useEffect } from 'react';
import { commentsAPI, moderationAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { MessageSquare, Shield, EyeOff, Trash2, Flag, Check, Brain, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminComments() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [aiScanning, setAiScanning] = useState(null);
  const [aiScores, setAiScores] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadComments(); }, [filter]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.filter_type = filter;
      const res = await commentsAPI.moderation(params);
      setComments(res.data.comments || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleHide = async (id) => {
    try {
      await commentsAPI.update(id, { is_hidden: true });
      toast.success('Comment hidden');
      loadComments();
    } catch { toast.error('Failed'); }
  };

  const handleUnhide = async (id) => {
    try {
      await commentsAPI.update(id, { is_hidden: false });
      toast.success('Comment visible again');
      loadComments();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await commentsAPI.delete(id);
      toast.success('Comment deleted');
      loadComments();
    } catch { toast.error('Failed'); }
  };

  const handleAIScan = async (commentId, content) => {
    setAiScanning(commentId);
    try {
      const res = await moderationAPI.checkComment({ content });
      const result = res.data;
      setAiScores(prev => ({ ...prev, [commentId]: result }));
      if (result.is_toxic || result.toxicity_score > 0.7) {
        toast.warning(`Toxic content detected (${Math.round((result.toxicity_score || 0) * 100)}% confidence)`);
      } else {
        toast.success(`Content OK (${Math.round((1 - (result.toxicity_score || 0)) * 100)}% safe)`);
      }
    } catch { toast.error('AI scan failed'); }
    finally { setAiScanning(null); }
  };

  const handleBulkAIScan = async () => {
    setBulkLoading(true);
    try {
      const contents = comments.map(c => ({ id: c.id, content: c.content }));
      const res = await moderationAPI.bulkCheck({ comments: contents });
      const results = res.data.results || [];
      const scores = {};
      let flagged = 0;
      results.forEach(r => {
        scores[r.id] = r;
        if (r.is_toxic || r.toxicity_score > 0.7) flagged++;
      });
      setAiScores(prev => ({ ...prev, ...scores }));
      toast.success(`Bulk scan complete: ${flagged} flagged, ${results.length - flagged} clean`);
    } catch { toast.error('Bulk AI scan failed'); }
    finally { setBulkLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Comment Moderation</h1>
          <p className="text-sm text-[#5B616E] mt-1">{total} comments</p>
        </div>
        <Button onClick={handleBulkAIScan} disabled={bulkLoading || comments.length === 0} className="bg-gradient-to-r from-[#1E5A8A] to-[#4A1D8A] text-white">
          {bulkLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
          AI Bulk Scan
        </Button>
      </div>

      <div className="flex gap-2">
        {[{ key: '', label: 'All' }, { key: 'reported', label: 'Reported' }, { key: 'hidden', label: 'Hidden' }].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
              filter === f.key ? 'bg-[#121316] text-white border-[#121316]' : 'bg-white text-[#5B616E] border-[#E7E0D6] hover:bg-[#E7E0D6]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : comments.length === 0 ? (
        <Card className="bg-white border-[#E7E0D6] p-12 text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-[#E7E0D6]" />
          <p className="text-[#5B616E]">No comments to moderate.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {comments.map(comment => (
            <Card key={comment.id} className={`bg-white border-[#E7E0D6] ${comment.is_hidden ? 'opacity-60' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  {(comment.avatar_url || comment.author?.avatar_url) ? (
                    <img src={comment.avatar_url || comment.author?.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#E7E0D6] flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {comment.author_name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{comment.author_name || 'Anonymous'}</span>
                      {comment.is_reported && <Badge className="text-[10px] bg-red-50 text-red-700"><Flag className="w-3 h-3 mr-1" /> Reported</Badge>}
                      {comment.is_hidden && <Badge className="text-[10px] bg-gray-100 text-gray-600"><EyeOff className="w-3 h-3 mr-1" /> Hidden</Badge>}
                    </div>
                    <p className="text-sm text-[#5B616E] mt-1">{comment.content}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-[#5B616E]">
                      {comment.article_title && <span>On: {comment.article_title}</span>}
                      <span>{new Date(comment.created_at).toLocaleString()}</span>
                      <span>{comment.likes || 0} likes</span>
                      <span>{comment.dislikes || 0} dislikes</span>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => handleAIScan(comment.id, comment.content)} disabled={aiScanning === comment.id} title="AI Toxicity Check">
                      {aiScanning === comment.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                    </Button>
                    {comment.is_hidden ? (
                      <Button size="sm" variant="ghost" onClick={() => handleUnhide(comment.id)}>
                        <Check className="w-4 h-4 mr-1" /> Show
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => handleHide(comment.id)}>
                        <EyeOff className="w-4 h-4 mr-1" /> Hide
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(comment.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {aiScores[comment.id] && (
                  <div className={`mt-2 ml-11 p-2 rounded-lg text-[10px] flex items-center gap-2 ${aiScores[comment.id].is_toxic ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                    {aiScores[comment.id].is_toxic ? <AlertTriangle className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                    <span>Toxicity: {Math.round((aiScores[comment.id].toxicity_score || 0) * 100)}%</span>
                    {aiScores[comment.id].categories && <span>| {aiScores[comment.id].categories.join(', ')}</span>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
